/// <reference path="../apple-pay.d.ts" />
/* eslint-disable @typescript-eslint/no-explicit-any -- Apple Pay SDK has no TypeScript definitions */
'use client';

import { useEffect, useRef } from 'react';

import { useRouter } from 'next/navigation';
import Script from 'next/script';

import { captureException } from '@sentry/nextjs';
import { useTranslation } from 'react-i18next';

import { toMinorUnits } from '@kit/shared/currency';
import { useTenantCurrency } from '@kit/ui/currency-icon';
import { buildPaymentDescription } from '@kit/payments/description';
import { toast } from '@kit/ui/sonner';

/**
 * Native Apple Pay button for FIXED-SLOT PACKAGE RENEWAL.
 *
 * Renewal is a package *purchase* under the hood: the server action
 * `createFixedSlotPackageRenewalCheckoutAction` has already authorized the
 * caller, reconstructed the slot server-side, minted `purchaseId`, and built
 * the full webhook `metadata` (type=`package_purchase`, is_renewal=true, the
 * fixed-slot schedule, price, …). This button is a dumb executor: it charges
 * the server-issued amount and forwards that server-issued `metadata` verbatim,
 * so the Moyasar webhook fulfils it identically to the renewal card path — no
 * client-built amounts or metadata that could drift from what was authorized.
 *
 * Mirrors `apple-pay-package-payment-button.tsx` (the house per-flow pattern).
 * Rendered for any Moyasar tenant once the checkout session exists — the parent
 * does NOT pre-gate on `canMakePayments()` (same as the new-package purchase
 * modal). Apple's SDK only draws a working button on Apple-Pay-capable Safari;
 * on other browsers the click-time `window.ApplePaySession` guard below shows a
 * fallback toast rather than crashing. Do not remove that guard.
 */
interface ApplePayPackageRenewalButtonProps {
  /** Server-authoritative charge amount (SAR). Never a client-derived value. */
  amount: number;
  /** Receipt / Apple Pay sheet label. */
  label: string;
  publishableKey: string;
  /** Server-issued purchase reference bound to the authorized renewal. */
  purchaseId: string;
  packageId: string;
  /**
   * Server-issued webhook metadata, forwarded to Moyasar unchanged. Already
   * carries type/purchase_id/package_id/user_id/is_renewal/fixed_slot/schedule
   * plus tenant_id + tenant_name (composed by the caller). Do NOT rebuild it
   * here — the server is the source of truth for what was authorized.
   */
  metadata: Record<string, string>;
}

function formatPaymentError(
  raw: string,
  t: (key: string, options?: { defaultValue: string }) => string,
): string {
  const normalized = raw.toUpperCase();

  if (
    normalized.includes('CARD COUNTRY NOT ALLOWED') ||
    normalized.includes('CARD_COUNTRY')
  ) {
    return t('common:memberClub.payment.errorCardCountry', {
      defaultValue:
        'Please use a Saudi-issued card or call us to confirm your reservation.',
    });
  }

  if (
    normalized.includes('INSUFFICIENT_FUNDS') ||
    normalized.includes('INSUFFICIENT FUNDS')
  ) {
    return t('common:memberClub.payment.errorInsufficientFunds', {
      defaultValue: 'Insufficient funds. Please try a different card.',
    });
  }

  if (normalized.includes('EXPIRED') || normalized.includes('CARD_EXPIRED')) {
    return t('common:memberClub.payment.errorCardExpired', {
      defaultValue: 'This card has expired. Please use a different card.',
    });
  }

  if (normalized.includes('BLOCKED') && !normalized.includes('CARD COUNTRY')) {
    return t('common:memberClub.payment.errorCardBlocked', {
      defaultValue: 'This card has been blocked. Please try a different card.',
    });
  }

  return raw;
}

// Safely call session.completePayment — Safari 26+ throws TypeError when the
// `errors` array carries plain objects instead of ApplePayError instances.
function safeCompletePayment(session: any, status: number) {
  try {
    session.completePayment({ status });
  } catch {
    // Silently ignore — the Apple Pay sheet closes regardless
  }
}

export function ApplePayPackageRenewalButton({
  amount,
  label,
  publishableKey,
  purchaseId,
  packageId,
  metadata,
}: ApplePayPackageRenewalButtonProps) {
  const router = useRouter();
  const tenantCurrency = useTenantCurrency();
  const { t } = useTranslation();
  const applePayButtonRef = useRef<HTMLElement | null>(null);

  const handleApplePayClick = async () => {
    if (!(window as any).ApplePaySession) {
      toast.error(
        t('common:memberClub.payment.toastApplePayUnavailable', {
          defaultValue: 'Apple Pay is not available on this device',
        }),
      );
      return;
    }

    // Moyasar is a Saudi PSP — settles in SAR only, matching the renewal card
    // path (which forces SAR via MoyasarCheckoutModal). PSP currency gating
    // belongs at the tenant currency-picker level.
    if (tenantCurrency !== 'SAR') {
      console.warn(
        `[Apple Pay] Tenant displays ${tenantCurrency} but Moyasar will charge SAR — amounts diverge (no FX conversion applied)`,
      );
    }

    const applePayPaymentRequest = {
      countryCode: 'SA',
      currencyCode: 'SAR',
      supportedNetworks: ['mada', 'visa', 'masterCard'],
      merchantCapabilities: ['supports3DS', 'supportsDebit', 'supportsCredit'],
      total: {
        label,
        amount: amount.toFixed(2),
      },
    };

    const session = new (window as any).ApplePaySession(
      5,
      applePayPaymentRequest,
    );

    let merchantValidated = false;
    let paymentFailed = false;

    session.onvalidatemerchant = async (event: any) => {
      const body = {
        validation_url: event.validationURL,
        display_name: 'Package Renewal',
        domain_name: window.location.hostname,
        publishable_api_key: publishableKey,
      };

      try {
        const response = await fetch(
          'https://api.moyasar.com/v1/applepay/initiate',
          {
            method: 'post',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
          },
        );

        if (!response.ok) {
          // Passing an error body to completeMerchantValidation crashes Safari —
          // abort instead so the user can fall back to the card form.
          const errorBody = await response.json().catch(() => ({}));
          console.error(
            '[Apple Pay] Renewal merchant validation rejected by Moyasar:',
            { status: response.status, error: errorBody },
          );
          toast.error(
            t('common:memberClub.payment.toastApplePayInitFailed', {
              defaultValue:
                'Apple Pay could not be initialized. Please try card payment.',
            }),
          );
          session.abort();
          return;
        }

        const merchantSession = await response.json();
        session.completeMerchantValidation(merchantSession);
        merchantValidated = true;
      } catch (error) {
        console.error('Merchant validation error:', error);
        captureException(error, {
          tags: {
            payment_method: 'applepay',
            error_type: 'merchant_validation_failed',
            booking_type: 'package_renewal',
          },
          extra: {
            domain: window.location.hostname,
            hasPublishableKey: !!publishableKey,
          },
        });
        toast.error(
          t('common:memberClub.payment.toastPaymentValidationFailed', {
            defaultValue: 'Payment validation failed',
          }),
        );
        session.abort();
      }
    };

    session.onpaymentauthorized = async (event: any) => {
      const token = event.payment.token;

      try {
        const paymentBody = {
          // Server-authoritative amount, charged in SAR (PSP constraint).
          amount: toMinorUnits(amount, 'SAR'),
          currency: 'SAR',
          description: buildPaymentDescription({
            surface: 'package_renewal',
            name: label,
          }),
          publishable_api_key: publishableKey,
          source: {
            type: 'applepay',
            token: token,
          },
          // Forwarded verbatim from the authorized server action — carries the
          // renewal schedule + purchase reference the webhook fulfils against.
          metadata,
        };

        const paymentResponse = await fetch(
          'https://api.moyasar.com/v1/payments',
          {
            method: 'post',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(paymentBody),
          },
        );

        const paymentResult = await paymentResponse.json();

        if (!paymentResponse.ok || paymentResult.status !== 'paid') {
          const rawReason =
            paymentResult.message ||
            paymentResult.source?.message ||
            t('common:memberClub.payment.errorPaymentFailedFallback', {
              defaultValue: 'Payment failed',
            });
          const failureReason = formatPaymentError(rawReason, t);
          paymentFailed = true;
          console.error('[Apple Pay] Package renewal payment failed:', {
            status: paymentResponse.status,
            paymentStatus: paymentResult.status,
            message: paymentResult.message,
            sourceMessage: paymentResult.source?.message,
            sourceType: paymentResult.source?.type,
          });
          captureException(
            new Error(`Apple Pay package renewal payment failed: ${rawReason}`),
            {
              tags: {
                provider: 'moyasar',
                payment_method: 'applepay',
                error_type: 'payment_failed',
                booking_type: 'package_renewal',
              },
              extra: {
                httpStatus: paymentResponse.status,
                paymentStatus: paymentResult.status,
                message: paymentResult.message,
                sourceMessage: paymentResult.source?.message,
              },
            },
          );
          toast.error(failureReason);
          safeCompletePayment(
            session,
            (window as any).ApplePaySession.STATUS_FAILURE,
          );
          // Nothing to unwind: the renewal package is only materialized by the
          // webhook on a `paid` status, so a failed charge leaves no orphan.
          return;
        }

        // Payment successful
        safeCompletePayment(
          session,
          (window as any).ApplePaySession.STATUS_SUCCESS,
        );

        toast.success(
          t('common:memberClub.payment.toastPaymentSuccessVerifying', {
            defaultValue: 'Payment successful! Verifying...',
          }),
        );
        // Same return path as the renewal card flow (moyasar-checkout-dialog's
        // purchaseId branch) + the payment id so the page can verify inline.
        router.push(
          `/book/return?type=package&purchase_id=${purchaseId}&package_id=${packageId}&id=${paymentResult.id}`,
        );
      } catch (error) {
        console.error('Payment error:', error);
        paymentFailed = true;
        captureException(error, {
          tags: {
            payment_method: 'applepay',
            error_type: 'package_renewal_error',
          },
        });
        const errorMessage =
          error instanceof Error
            ? error.message
            : t('common:memberClub.payment.errorPaymentProcessing', {
                defaultValue: 'Payment processing failed',
              });
        toast.error(errorMessage);
        safeCompletePayment(
          session,
          (window as any).ApplePaySession.STATUS_FAILURE,
        );
      }
    };

    session.oncancel = () => {
      if (paymentFailed) {
        return;
      }

      if (!merchantValidated) {
        console.error(
          '[Apple Pay] Renewal sheet dismissed before merchant validation',
          { domain: window.location.hostname },
        );
        toast.error(
          t('common:memberClub.payment.toastApplePayInitFailed', {
            defaultValue:
              'Apple Pay could not be initialized. Please try card payment.',
          }),
        );
      } else {
        console.warn('[Apple Pay] Renewal payment cancelled by user');
      }
    };

    session.begin();
  };

  // Attach the click handler to the native <apple-pay-button> once on mount.
  // The consumed props are stable references from the parent (resolved from a
  // server action result and never mutated after this button mounts), so the
  // empty-dependency attach carries no stale-closure risk — same rationale as
  // apple-pay-package-payment-button.tsx.
  useEffect(() => {
    const button = applePayButtonRef.current;
    if (button) {
      button.addEventListener('click', handleApplePayClick);

      return () => {
        button.removeEventListener('click', handleApplePayClick);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {/* Apple Pay SDK — defines the <apple-pay-button> custom element. Loaded
          here so the button is self-contained (the /packages renewal path does
          not otherwise load it, unlike the payment pages). Next dedupes by src,
          so it is safe next to other Apple Pay buttons loading the same script. */}
      <Script
        src="https://applepay.cdn-apple.com/jsapi/v1/apple-pay-sdk.js"
        strategy="lazyOnload"
      />
      {/* dir="ltr": keep the native <apple-pay-button> LTR even when the page is
          dir="rtl" (Arabic), so Apple's shadow-DOM glyph + text don't flip. */}
      <div dir="ltr" className="w-full">
      <style>
        {`
          apple-pay-button {
            --apple-pay-button-width: 100%;
            --apple-pay-button-height: 40px;
            --apple-pay-button-border-radius: var(--native-booking-control-radius, 6px);
            --apple-pay-button-padding: 0px 0px;
            --apple-pay-button-box-sizing: border-box;
            display: block;
            margin: 0;
          }
        `}
      </style>
      <apple-pay-button
        ref={(el: HTMLElement | null) => {
          applePayButtonRef.current = el;
        }}
        buttonstyle="black"
        type="pay"
        locale="en"
        data-test="apple-pay-package-renewal-button"
      />
      </div>
    </>
  );
}
